import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { db } from '../../firebase/client';
import { doc, updateDoc, collection, addDoc, serverTimestamp, query, where, onSnapshot } from 'firebase/firestore';
import { AppContext } from '../../context/AppContext';
import { useAllOrders } from '../../context/AllOrdersContext';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDateReadable } from '../../utils/dateUtils';
import {
    ArrowLeft,
    CheckCircle2,
    Clock,
    AlertTriangle,
    ExternalLink,
    Upload,
    X,
    CreditCard,
    Rocket,
    Zap,
    TrendingUp,
    ShieldCheck,
    Calendar,
    History,
    MessageSquare,
    User
} from 'lucide-react';

const SubscriptionScreen: React.FC = () => {
    const { currentUser } = useAuth();
    const { getRestaurantById, restaurants, uploadImage } = useData();
    const { placedOrders } = useAllOrders();
    const { view, setView } = React.useContext(AppContext);

    const restaurant = getRestaurantById(currentUser?.restaurantId || '') || 
        restaurants.find(r => r.ownerId === currentUser?.uid || r.owner_id === currentUser?.uid);

    if (!restaurant) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
            <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mb-4"
            />
            <p className="text-gray-500 font-bold">Chargement des données du restaurant...</p>
        </div>
    );

    // 1. ALL HOOKS MUST BE AT THE TOP
    const [activeTab, setActiveTab] = useState<'subscription' | 'featured'>('subscription');
    const [selectedPlan, setSelectedPlan] = useState<'mensuel' | 'annuel' | 'commission'>('mensuel');
    const [selectedBoost, setSelectedBoost] = useState<'1_week' | '15_days' | '3_weeks' | '1_month'>('1_week');

    const [isUploading, setIsUploading] = useState(false);
    const [proofUrl, setProofUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [currentStep, setCurrentStep] = useState(1);
    const [hasPendingPayment, setHasPendingPayment] = useState(false);

    // Monitor pending payments in real-time
    React.useEffect(() => {
        if (!restaurant?.id) return;
        const q = query(
            collection(db, 'payments'), 
            where('restaurantId', '==', restaurant.id),
            where('status', '==', 'pending')
        );
        const unsub = onSnapshot(q, (snap) => {
            setHasPendingPayment(!snap.empty);
        }, (err) => {
            console.error("Error listening to payments:", err);
        });
        return () => unsub();
    }, [restaurant?.id]);

    // Persistence Keys
    const SUB_STATE_KEY = `yumii_sub_state_${restaurant?.id}`;

    // Pre-select plan from params if provided
    React.useEffect(() => {
        // 1. Restore from localStorage first
        try {
            const saved = localStorage.getItem(SUB_STATE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                
                // If we are entering via a specific mode (renewal/switch), 
                // we should ignore/clear the old state to avoid confusion
                if (view.params?.mode) {
                    localStorage.removeItem(SUB_STATE_KEY);
                    console.log('[Persistence] Mode detected, clearing old state');
                } else {
                    if (parsed.currentStep) setCurrentStep(parsed.currentStep);
                    if (parsed.selectedPlan) setSelectedPlan(parsed.selectedPlan);
                    if (parsed.selectedBoost) setSelectedBoost(parsed.selectedBoost);
                    if (parsed.proofUrl) setProofUrl(parsed.proofUrl);
                    if (parsed.activeTab) setActiveTab(parsed.activeTab);
                    console.log('[Persistence] Restored sub state:', parsed);
                }
            }
        } catch (e) {
            console.error('[Persistence] Error restoring sub state:', e);
        }

        // 2. Parameters override EVERYTHING
        if (view.params?.planType) {
            setSelectedPlan(view.params.planType);
        }
        if (view.params?.activeTab) {
            setActiveTab(view.params.activeTab as any);
        }
    }, [view.params?.planType, view.params?.activeTab, view.params?.mode, restaurant?.id]);

    // Save to localStorage on change
    React.useEffect(() => {
        if (!restaurant?.id) return;
        const state = {
            currentStep,
            selectedPlan,
            selectedBoost,
            proofUrl,
            activeTab
        };
        localStorage.setItem(SUB_STATE_KEY, JSON.stringify(state));
    }, [currentStep, selectedPlan, selectedBoost, proofUrl, activeTab, restaurant?.id]);

    // Calculate trial days left
    const getTrialDaysLeft = () => {
        if (!restaurant?.trialExpiry) return 0;
        const now = new Date();
        const expiry = restaurant.trialExpiry.seconds
            ? new Date(restaurant.trialExpiry.seconds * 1000)
            : new Date(restaurant.trialExpiry);
        const diffTime = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const trialDaysLeft = getTrialDaysLeft();

    const getTimeRemaining = (expiry: any) => {
        if (!expiry) return null;
        const expiryDate = expiry.seconds ? new Date(expiry.seconds * 1000) : new Date(expiry);
        const now = new Date();
        const diff = expiryDate.getTime() - now.getTime();
        if (diff <= 0) return null;

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days >= 1) return `${days}j restant${days > 1 ? 's' : ''}`;

        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours >= 1) return `${hours}h restante${hours > 1 ? 's' : ''}`;

        const minutes = Math.floor(diff / (1000 * 60));
        return `${minutes}m restante${minutes > 1 ? 's' : ''}`;
    };

    const isTrialExpired = restaurant?.subscriptionStatus === 'trial' && trialDaysLeft <= 0;
    const isPending = restaurant?.subscriptionStatus === 'pending';
    const isActive = restaurant?.subscriptionStatus === 'active';
    const isTrial = restaurant?.subscriptionStatus === 'trial' || (restaurant as any).trialPeriod;
    const hasActivePeriod = isActive || isTrial;
    const isSubscriptionRenewalPending = isActive && (restaurant as any).pendingSubscriptionRenewal;
    const isBoostPending = restaurant?.featuredStatus === 'pending';
    const isBoostActive = restaurant?.featuredStatus === 'active';
    const isBoostRenewalPending = isBoostActive && (restaurant as any).pendingFeaturedRenewal;

    // Calculate Commission Amount (5% of delivered/completed orders)
    const calculateCommission = () => {
        if (!restaurant) return 0;
        const commStart = restaurant.commissionStartedAt?.toDate ? restaurant.commissionStartedAt.toDate() : (restaurant.commissionStartedAt ? new Date(restaurant.commissionStartedAt) : null);
        
        const deliveredOrders = placedOrders?.filter(o => {
            if (o.restaurantId !== restaurant.id) return false;
            
            // Financial Scope: Only delivered or completed orders
            if (o.status !== 'Livrée' && o.status !== 'Terminée') return false;
            
            if (commStart) {
                const ts = o.timestamp as any;
                const d = ts instanceof Date ? ts : (ts?.seconds ? new Date(ts.seconds * 1000) : ts ? new Date(ts) : null);
                // Only count orders since the last commission payment/activation
                if (!d || d < commStart) return false;
            }
            return true;
        }) || [];

        const totalTurnover = deliveredOrders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
        const currentMonthCommission = Math.round(totalTurnover * 0.05);
        
        // Ensure debt is treated as a number
        const debt = Number(restaurant.commissionDebt || 0);
        return Math.max(0, currentMonthCommission + debt);
    };

    // --- LOGIC: TOTAL AMOUNT CALCULATION ---
    const calculateTotalAmount = () => {
        if (activeTab === 'featured') {
            const featuredPrices = {
                '1_week': 5000,
                '15_days': 8500,
                '3_weeks': 12000,
                '1_month': 15000
            };
            return featuredPrices[selectedBoost as keyof typeof featuredPrices] || 0;
        }

        // Subscription Tab
        const debt = Number(view.params?.debt || 0);
        const isCumulative = view.params?.isCumulative || view.params?.mode === 'switch_to_fixed';

        if (selectedPlan === 'commission') {
            // For commission renewal, we might have a specific amount passed in params
            if (view.params?.mode === 'renew' && view.params?.amount) {
                return Number(view.params.amount);
            }
            return calculateCommission(); // This already includes restaurant.commissionDebt
        }

        // Fixed Plans (Monthly/Annual)
        const basePrice = selectedPlan === 'mensuel' ? 5000 : 50000;
        return isCumulative ? (basePrice + debt) : basePrice;
    };

    const totalAmount = calculateTotalAmount();

    const handleProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        try {
            setIsUploading(true);
            setErrorMessage('');
            const url = await uploadImage(file);
            setProofUrl(url);
        } catch (error) {
            console.error("Upload error:", error);
            setErrorMessage("Erreur lors de l'upload de la preuve.");
        } finally {
            setIsUploading(false);
        }
    };

    // --- LOGIC: DATE CALCULATIONS ---
    const getDurationInDays = (plan: string) => {
        switch (plan) {
            case 'mensuel': return 30;
            case 'annuel': return 365;
            case 'commission': return 30;
            case '1_week': return 7;
            case '15_days': return 15;
            case '3_weeks': return 21;
            case '1_month': return 30;
            default: return 30;
        }
    };

    const getPlannedStartDate = (type: 'subscription' | 'featured') => {
        const now = new Date();
        const getExpiryDate = (timestamp: any) => {
            if (!timestamp) return null;
            return timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
        };

        if (type === 'subscription') {
            const trialExp = getExpiryDate(restaurant?.trialExpiry);
            const subExp = getExpiryDate(restaurant?.subscriptionExpiry);
            
            let latestFirmDate = now;
            
            // If in trial, everything starts after trial
            if (trialExp && trialExp > latestFirmDate) {
                latestFirmDate = trialExp;
            }
            
            // If already has an active subscription (validated), chain after it
            // This handles the case: Trial -> Monthly (Active) -> Commission (Starts after Monthly)
            if (subExp && subExp > latestFirmDate) {
                latestFirmDate = subExp;
            }
            return latestFirmDate;
        } else {
            const featuredExp = getExpiryDate(restaurant?.featuredExpiry);
            
            let latestFirmDate = now;
            if (featuredExp && featuredExp > latestFirmDate) {
                latestFirmDate = featuredExp;
            }
            return latestFirmDate;
        }
    };

    const plannedStartDate = getPlannedStartDate(activeTab);
    const isRenewal = view.params?.mode === 'renew';
    const isInitialCommission = activeTab === 'subscription' &&
        selectedPlan === 'commission' &&
        restaurant.subscriptionType !== 'commission' &&
        !isRenewal;

    const handleSubmitPayment = async () => {
        if (hasPendingPayment) {
            setErrorMessage("Vous avez déjà un règlement en attente de validation.");
            return;
        }

        if (!proofUrl && !isInitialCommission) {
            setErrorMessage("Veuillez uploader une preuve de paiement.");
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');
        try {
            const now = new Date();
            const startRef = plannedStartDate;

            if (activeTab === 'subscription') {
                const isRenewal = hasActivePeriod;

                // 1. Prepare Restaurant Update
                const updateData: any = {
                    updatedAt: serverTimestamp(),
                    pendingPaymentProof: proofUrl || 'auto_commission'
                };

                if (isRenewal) {
                    updateData.pendingSubscriptionType = selectedPlan;
                    updateData.pendingSubscriptionRenewal = true;
                } else {
                    updateData.subscriptionStatus = 'pending';
                    updateData.subscriptionType = selectedPlan;
                }

                await updateDoc(doc(db, 'restaurants', restaurant.id), updateData);

                // 2. Add to payments collection
                const durationDays = getDurationInDays(selectedPlan);
                const endRef = new Date(startRef.getTime() + durationDays * 24 * 60 * 60 * 1000);

                let contextDescription = '';
                const currentPlan = restaurant.subscriptionType || 'aucun';
                
                if (selectedPlan === 'commission') {
                    if (currentPlan === 'mensuel' || currentPlan === 'annuel') {
                        contextDescription = `CHANGEMENT DE PLAN : de ${currentPlan.toUpperCase()} vers COMMISSION`;
                    } else if (!isRenewal) {
                        contextDescription = `NOUVEL ABONNEMENT : Commission (1ère fois, ancien: ${currentPlan})`;
                    }
                } else if (selectedPlan === 'mensuel' || selectedPlan === 'annuel') {
                    if (currentPlan === 'commission') {
                        contextDescription = `CHANGEMENT DE PLAN : de COMMISSION vers ${selectedPlan.toUpperCase()}`;
                    } else if (!isRenewal) {
                        contextDescription = `NOUVEL ABONNEMENT : ${selectedPlan.toUpperCase()} (1ère fois, ancien: ${currentPlan})`;
                    }
                }
                
                const finalDescription = view.params?.description 
                    ? (contextDescription ? `${contextDescription} | Détails: ${view.params.description}` : view.params.description)
                    : contextDescription;

                await addDoc(collection(db, 'payments'), {
                    restaurantId: restaurant.id,
                    restaurantName: restaurant.name,
                    type: 'subscription',
                    category: isInitialCommission ? 'commission_activation' : (isRenewal ? 'subscription_renewal' : 'subscription_initial'),
                    plan: selectedPlan,
                    planType: selectedPlan,
                    period: selectedPlan === 'mensuel' ? '1 mois' : selectedPlan === 'annuel' ? '1 an' : 'Commission',
                    amount: totalAmount,
                    proofUrl: proofUrl || 'auto_commission',
                    status: 'pending',
                    startDate: startRef,
                    endDate: endRef,
                    isCumulative: view.params?.isCumulative || false,
                    description: finalDescription,
                    timestamp: serverTimestamp(),
                    createdAt: serverTimestamp()
                });

                if (isInitialCommission) {
                    setSuccessMessage("Votre demande d'activation de l'abonnement Commission a été envoyée !");
                } else {
                    setSuccessMessage(isRenewal ? "Votre demande de renouvellement d'abonnement a été envoyée !" : "Votre preuve de paiement d'abonnement a été envoyée !");
                }
            } else {
                const isFeaturedRenewal = isBoostActive;
                const featuredPrices = {
                    '1_week': 5000,
                    '15_days': 8500,
                    '3_weeks': 12000,
                    '1_month': 15000
                };

                const updateData: any = {
                    pendingBoostProof: proofUrl,
                    updatedAt: serverTimestamp()
                };

                if (isFeaturedRenewal) {
                    updateData.pendingFeaturedType = selectedBoost;
                    updateData.pendingFeaturedRenewal = true;
                } else {
                    updateData.featuredStatus = 'pending';
                    updateData.featuredType = selectedBoost;
                }

                await updateDoc(doc(db, 'restaurants', restaurant.id), updateData);

                await addDoc(collection(db, 'payments'), {
                    restaurantId: restaurant.id,
                    restaurantName: restaurant.name,
                    type: 'featured',
                    category: isFeaturedRenewal ? 'featured_renewal' : 'featured_initial',
                    plan: selectedBoost,
                    planType: selectedBoost === '1_week' ? 'Une semaine' :
                        selectedBoost === '15_days' ? '15 jours' :
                            selectedBoost === '3_weeks' ? '3 semaines' : 'Un mois',
                    period: selectedBoost === '1_week' ? '7 jours' :
                        selectedBoost === '15_days' ? '15 jours' :
                            selectedBoost === '3_weeks' ? '3 semaines' : '1 mois',
                    amount: featuredPrices[selectedBoost as keyof typeof featuredPrices],
                    proofUrl: proofUrl,
                    status: 'pending',
                    startDate: startRef, // Important for admin to know when to start it
                    timestamp: serverTimestamp(),
                    createdAt: serverTimestamp()
                });

                setSuccessMessage(isFeaturedRenewal ? "Votre demande de renouvellement En Vedette a été envoyée !" : "Votre demande En Vedette a été envoyée !");
            }

            setProofUrl('');
            
            // Clear persistence
            localStorage.removeItem(SUB_STATE_KEY);
            setCurrentStep(1);

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccessMessage('');
                setView({ screen: 'adminPayments' }); // Redirect to payment history
            }, 3000);
        } catch (error) {
            console.error("Submit error:", error);
            setErrorMessage("Une erreur est survenue lors de l'envoi.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getSubmitButtonLabel = () => {
        if (isSubmitting) return "TRANSMISSION...";
        if (activeTab === 'subscription') {
            return isActive ? "RENOUVELER MON ABONNEMENT" : "VALIDER LE PAIEMENT";
        } else {
            return isBoostActive ? "RENOUVELER EN VEDETTE" : "VALIDER EN VEDETTE";
        }
    };

    // 2. CONDITIONAL RETURNS MUST BE AFTER ALL HOOKS
    if (hasPendingPayment) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[500px] p-8 text-center bg-gray-50/50 backdrop-blur-sm">
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-full max-w-sm bg-white p-10 rounded-[3rem] shadow-2xl border border-gray-100 relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400" />
                    <div className="w-20 h-20 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner border border-orange-100">
                        <Clock className="w-10 h-10 text-orange-600 animate-pulse" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter italic mb-4 leading-none">
                        Opération en <br/> <span className="text-orange-600">attente</span>
                    </h2>
                    <p className="text-[10px] text-gray-500 font-black leading-loose uppercase mb-10 tracking-wide text-balance">
                        Un règlement est déjà en attente de validation par l'administrateur. 
                        <span className="block mt-4 text-gray-400 italic">
                            Le bouton "Continuer" a été temporairement masqué pour éviter tout doublon.
                        </span>
                    </p>
                    <button 
                        onClick={() => setView({ screen: 'adminPayments' })}
                        className="w-full py-5 bg-orange-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest italic shadow-xl shadow-orange-200 hover:bg-orange-700 active:scale-95 transition-all"
                    >
                        Accéder à l'Historique
                    </button>
                    <button 
                        onClick={() => setView({ screen: 'adminHome' })}
                        className="mt-6 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] hover:text-red-600 transition-colors"
                    >
                        Retour au Tableau de Bord
                    </button>
                </motion.div>
            </div>
        );
    }

    if (!restaurant) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full"
            />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-32">
            {/* Header */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-20 px-6 py-5 flex items-center gap-4">
                <button
                    onClick={() => setView({ screen: 'adminAccount' })}
                    className="p-2.5 rounded-2xl bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-700 transition-all border border-gray-100"
                >
                    <ArrowLeft size={24} />
                </button>
                <div className="">
                    <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter">Gestion Abonnement</h1>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Restaurant: {restaurant.name}</p>
                </div>
            </div>

            <div className="max-w-xl mx-auto p-6 space-y-8">

                {/* Feedback Messages */}
                <AnimatePresence>
                    {successMessage && (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: -20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-green-500 text-white p-4 rounded-2xl flex items-center gap-3 shadow-xl shadow-green-200 border border-green-400"
                        >
                            <CheckCircle2 size={24} className="shrink-0" />
                            <p className="font-black text-sm">{successMessage}</p>
                        </motion.div>
                    )}
                    {errorMessage && (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: -20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-red-500 text-white p-4 rounded-2xl flex items-center gap-3 shadow-xl shadow-red-200 border border-red-400"
                        >
                            <AlertTriangle size={24} className="shrink-0" />
                            <p className="font-black text-sm">{errorMessage}</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pending Warning Banner */}
                {hasPendingPayment && (
                    <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-[2rem] flex items-start gap-4 shadow-sm"
                    >
                        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center shrink-0">
                            <AlertTriangle className="text-orange-600 w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-xs font-black text-orange-900 uppercase tracking-tight italic mb-1">Paiement en cours</h3>
                            <p className="text-[10px] text-orange-700 font-medium leading-relaxed uppercase">
                                Un règlement est déjà en attente de validation par l'administrateur. 
                            </p>
                            <button 
                                onClick={() => setView({ screen: 'adminPayments' })}
                                className="mt-2 text-[9px] font-black text-orange-600 underline underline-offset-4 uppercase tracking-widest italic"
                            >
                                Voir l'Historique
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Compact Status Header */}
                {currentStep !== 1 && (
                    <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 overflow-hidden relative">
                        <div className="flex items-center justify-between relative z-10">
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-2xl ${hasActivePeriod ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                    {hasActivePeriod ? <ShieldCheck size={24} /> : <AlertTriangle size={24} />}
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Statut Actuel</h4>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-black text-gray-900 uppercase italic">
                                            {isTrial ? 'Essai Gratuit' : 
                                            restaurant.subscriptionStatus === 'active' ? 'Abonnement Actif' : 
                                            isPending ? 'Paiement en attente' : 'Expiré'}
                                        </span>
                                        {hasActivePeriod && (
                                            <div className="bg-green-50 px-2.5 py-1 rounded-full border border-green-100 flex items-center gap-1.5 shadow-sm ml-2">
                                                <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse" />
                                                <span className="text-[9px] font-black text-green-700 uppercase tracking-tighter whitespace-nowrap">
                                                    {getTimeRemaining(restaurant?.currentPeriodExpiry)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {hasActivePeriod && (
                                <div className="text-right">
                                    <span className="block text-[8px] font-black text-gray-400 uppercase tracking-tighter">Échéance</span>
                                    <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-tight">
                                        Expire le {formatDateReadable(restaurant.subscriptionExpiry || restaurant.trialExpiry)}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Queued indicator if exists */}
                        {restaurant.subscriptionQueue && restaurant.subscriptionQueue.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-50 flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                                <span className="text-[9px] font-bold text-orange-900 uppercase tracking-wide">
                                    {restaurant.subscriptionQueue.length} abonnement(s) en attente d'activation
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Tabs Selector - Hidden if a specific mode or tab is targeted (Renewal/Switch) */}
                {(!view.params?.mode && !view.params?.activeTab) && (
                    <div className="flex p-1 bg-gray-100 rounded-3xl gap-1">
                        <button
                            onClick={() => setActiveTab('subscription')}
                            className={`flex-1 py-3 px-4 rounded-[1.25rem] flex items-center justify-center gap-2 font-black text-xs transition-all ${activeTab === 'subscription' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                        >
                            <ShieldCheck size={14} />
                            FORFAITS
                        </button>
                        <button
                            onClick={() => setActiveTab('featured')}
                            className={`flex-1 py-3 px-4 rounded-[1.25rem] flex items-center justify-center gap-2 font-black text-xs transition-all ${activeTab === 'featured' ? 'bg-indigo-600 text-white shadow-sm' : 'text-indigo-400 hover:text-indigo-600'}`}
                        >
                            <Zap size={14} />
                            EN VEDETTE
                        </button>
                    </div>
                )}

                {/* Step Manager */}
                <AnimatePresence mode="wait">
                    {currentStep === 1 ? (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="space-y-6"
                        >
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-2 italic">1. Choisissez votre plan</p>
                            <AnimatePresence mode="wait">
                        {activeTab === 'subscription' ? (
                            <motion.div
                                key="subscription"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                className="grid grid-cols-1 gap-3"
                            >
                                {[
                                    { 
                                        id: 'mensuel', 
                                        title: 'Mensuel', 
                                        price: (view.params?.mode === 'switch_to_fixed' || view.params?.isCumulative)
                                            ? `${(5000 + Number(view.params?.debt || 0)).toLocaleString()} DA` 
                                            : '5 000 DA', 
                                        desc: (view.params?.mode === 'switch_to_fixed' || view.params?.isCumulative) ? 'TOTAL À RÉGLER' : '/ mois', 
                                        icon: Calendar 
                                    },
                                    { 
                                        id: 'annuel', 
                                        title: 'Annuel', 
                                        price: (view.params?.mode === 'switch_to_fixed' || view.params?.isCumulative)
                                            ? `${(50000 + Number(view.params?.debt || 0)).toLocaleString()} DA` 
                                            : '50 000 DA', 
                                        desc: (view.params?.mode === 'switch_to_fixed' || view.params?.isCumulative) ? 'TOTAL À RÉGLER' : '/ an', 
                                        icon: ShieldCheck, 
                                        promo: 'Économisez 10 000 DA' 
                                    },
                                    { 
                                        id: 'commission', 
                                        title: 'Commission', 
                                        price: view.params?.mode === 'renew' ? `${calculateCommission().toLocaleString()} DA` : '5%', 
                                        desc: view.params?.mode === 'renew' ? 'TOTAL À RÉGLER' : 'PAR VENTE (5%)', 
                                        icon: TrendingUp 
                                    }
                                ].filter(plan => {
                                    // Rule: If in renewal mode and currently commission, ONLY show commission
                                    if (view.params?.mode === 'renew' && restaurant.subscriptionType === 'commission') {
                                        return plan.id === 'commission';
                                    }
                                    if (view.params?.mode === 'renew_fixed' && plan.id === 'commission') return false;
                                    
                                    // Rule: If switching from commission to fixed, only show fixed plans
                                    if (view.params?.mode === 'switch_to_fixed') {
                                        return plan.id !== 'commission';
                                    }

                                    // Rule: If switching to commission, only show commission
                                    if (view.params?.mode === 'switch_to_commission') {
                                        return plan.id === 'commission';
                                    }

                                    return true;
                                }).map((plan: any) => (
                                    <button
                                        key={plan.id}
                                        onClick={() => setSelectedPlan(plan.id)}
                                        className={`p-5 rounded-[2rem] border-2 text-left transition-all flex items-center justify-between group ${selectedPlan === plan.id ? 'border-red-600 bg-white shadow-lg' : 'border-gray-100 bg-white hover:border-red-200'}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${selectedPlan === plan.id ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-400'}`}>
                                                <plan.icon size={24} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-sm font-black text-gray-900 uppercase italic">{plan.title}</h3>
                                                    {plan.promo && <span className="text-[7px] font-black bg-red-600 text-white px-1.5 py-0.5 rounded-full uppercase">{plan.promo}</span>}
                                                </div>
                                                <div className="flex items-baseline gap-1">
                                                    <span className={`text-lg font-black ${selectedPlan === plan.id ? 'text-red-600' : 'text-gray-900'}`}>{plan.price}</span>
                                                    <span className="text-[10px] text-gray-400 font-bold">{plan.desc}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedPlan === plan.id ? 'border-red-600 bg-red-600 shadow-sm shadow-red-100' : 'border-gray-200'}`}>
                                            {selectedPlan === plan.id && <CheckCircle2 size={12} className="text-white" />}
                                        </div>
                                    </button>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                exit={{ opacity: 0, x: 10 }}
                                className="grid grid-cols-1 gap-3"
                            >
                                {[
                                    { id: '1_week', title: '7 Jours', price: '5 000 DA', icon: Zap },
                                    { id: '15_days', title: '15 Jours', price: '8 500 DA', icon: Calendar },
                                    { id: '3_weeks', title: '3 Semaines', price: '12 000 DA', icon: TrendingUp },
                                    { id: '1_month', title: '1 Mois', price: '15 000 DA', icon: Rocket }
                                ].map((boost: any) => (
                                    <button
                                        key={boost.id}
                                        onClick={() => setSelectedBoost(boost.id)}
                                        className={`p-4 rounded-[1.75rem] border-2 text-left transition-all ${selectedBoost === boost.id ? 'border-indigo-600 bg-white shadow-lg' : 'border-gray-100 bg-white hover:border-indigo-100'}`}
                                    >
                                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-3 ${selectedBoost === boost.id ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-50 text-gray-400'}`}>
                                            <boost.icon size={18} />
                                        </div>
                                        <h3 className="text-xs font-black text-gray-900 uppercase italic leading-none mb-1">{boost.title}</h3>
                                        <span className={`text-sm font-black ${selectedBoost === boost.id ? 'text-indigo-600' : 'text-gray-900'}`}>{boost.price}</span>
                                    </button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>

                            {/* Navigation to Step 2 or Confirm for Commission */}
                            {isInitialCommission ? (
                                <button
                                    onClick={handleSubmitPayment}
                                    disabled={hasPendingPayment || isSubmitting}
                                    className={`w-full py-5 rounded-[2rem] font-black text-lg shadow-xl shadow-red-100 flex items-center justify-center gap-3 uppercase italic tracking-tight transition-all ${hasPendingPayment || isSubmitting ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700 active:scale-95'}`}
                                >
                                    {isSubmitting ? "TRANSMISSION..." : "CONFIRMER"}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setCurrentStep(2)}
                                    disabled={hasPendingPayment}
                                    className={`w-full py-5 rounded-[2rem] font-black text-lg shadow-xl shadow-red-100 flex items-center justify-center gap-3 uppercase italic tracking-tight transition-all ${hasPendingPayment ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700 active:scale-95'}`}
                                >
                                    Continuer vers le paiement
                                    <ArrowLeft size={24} className="rotate-180" />
                                </button>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div className="flex items-center justify-between px-2">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] italic">2. Règlement & Preuve</p>
                            </div>
                    
                    <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-red-100 border border-gray-100 overflow-hidden">
                        {/* Premium Summary Bar */}
                        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6 flex items-center justify-between relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/10 blur-[60px] rounded-full -mr-16 -mt-16" />
                            <div className="flex items-center gap-4 relative z-10">
                                <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-xl border border-white/10 shadow-inner">
                                    <CreditCard size={24} className="text-red-500" />
                                </div>
                                <div className="text-left">
                                    <h5 className="text-2xl font-black text-white italic leading-none tracking-tighter">
                                        {totalAmount.toLocaleString()} DA
                                    </h5>
                                    
                                    {activeTab === 'subscription' && (selectedPlan === 'commission' || view.params?.isCumulative || view.params?.mode === 'switch_to_fixed') && (Number(restaurant.commissionDebt || 0) > 0 || Number(view.params?.debt || 0) > 0) && (
                                        <div className="mt-2 py-1 px-2.5 bg-red-500/20 rounded-lg border border-red-500/30 backdrop-blur-sm">
                                            <p className="text-[8px] font-black text-red-200 uppercase tracking-widest text-center italic">
                                                Inclut {Number(view.params?.debt || restaurant.commissionDebt).toLocaleString()} DA de dettes précédentes
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="text-right relative z-10">
                                <span className="block text-[9px] font-bold text-white/30 uppercase tracking-tighter mb-1">Bénéficiaire</span>
                                <div className="bg-white/5 px-2 py-1 rounded-lg border border-white/5">
                                    <span className="text-[10px] font-black text-white uppercase tracking-wider">Yumii Food</span>
                                </div>
                            </div>
                        </div>

                        {/* Internal Payment Guide / Upload */}
                        <div className="p-6 space-y-6">
                            {/* Bank Account Grid */}
                            <div className="grid grid-cols-1 gap-2">
                                <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-[8px] font-black text-gray-400 uppercase">Nom & Prénom</p>
                                        <p className="text-xs font-black text-gray-900 tracking-wider">John Doe</p>
                                    </div>
                                    <div className="bg-white p-1.5 rounded-lg border border-gray-100"><User size={12} className="text-gray-300" /></div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-[8px] font-black text-gray-400 uppercase">CCP</p>
                                        <p className="text-xs font-black text-gray-900 tracking-wider">0022878434 Clé 20</p>
                                    </div>
                                    <div className="bg-white p-1.5 rounded-lg border border-gray-100"><ExternalLink size={12} className="text-gray-300" /></div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-[8px] font-black text-gray-400 uppercase">RIP</p>
                                        <p className="text-[10px] font-black text-gray-900 font-mono tracking-tighter">007 99999 0022878434 20</p>
                                    </div>
                                    <div className="bg-white p-1.5 rounded-lg border border-gray-100"><ExternalLink size={12} className="text-gray-300" /></div>
                                </div>
                            </div>

                            {/* Upload Area */}
                            <div className="space-y-4">
                                {activeTab === 'subscription' && selectedPlan === 'commission' && restaurant.subscriptionType !== 'commission' ? (
                                    <div className="bg-red-50 p-6 rounded-[2rem] border-2 border-red-100 relative overflow-hidden group">
                                        <TrendingUp size={80} className="absolute -bottom-4 -right-4 text-red-500/10 group-hover:scale-110 transition-transform" />
                                        <p className="text-sm text-red-900 font-black leading-tight italic mb-2">Activation Immédiate</p>
                                        <p className="text-[10px] text-red-700 font-medium leading-relaxed italic">
                                            Le passage à la commission est une demande d'activation. Aucun paiement n'est requis aujourd'hui. L'admin validera votre passage sous 24h.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        {isUploading && (
                                            <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-3xl">
                                                <div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin mb-2" />
                                                <span className="text-[8px] font-black text-red-600 uppercase">Téléchargement...</span>
                                            </div>
                                        )}
                                        {proofUrl ? (
                                            <div className="relative aspect-video rounded-3xl overflow-hidden border-2 border-green-500 shadow-xl">
                                                <img src={proofUrl} alt="Reçu" className="w-full h-full object-cover" />
                                                <button onClick={() => setProofUrl('')} className="absolute top-2 right-2 w-8 h-8 rounded-xl bg-red-500 text-white flex items-center justify-center shadow-lg"><X size={16} /></button>
                                            </div>
                                        ) : (
                                            <label htmlFor="subscription-proof-upload" className="flex flex-col items-center justify-center w-full aspect-video bg-gray-50 border-2 border-dashed border-gray-200 rounded-[2rem] cursor-pointer hover:bg-gray-100 transition-all group">
                                                <Upload size={24} className="text-gray-400 group-hover:text-red-600 mb-2" />
                                                <span className="text-[10px] font-black text-gray-600 uppercase">Joindre le reçu de paiement</span>
                                            </label>
                                        )}
                                        <input id="subscription-proof-upload" type="file" className="hidden" accept="image/*,application/pdf" onChange={handleProofUpload} />
                                    </div>
                                )}
                            </div>

                            {/* Submission Button */}
                            <button
                                onClick={handleSubmitPayment}
                                disabled={isSubmitting || isUploading || hasPendingPayment || (proofUrl === '' && !(activeTab === 'subscription' && selectedPlan === 'commission' && restaurant.subscriptionType !== 'commission' && view.params?.mode !== 'renew'))}
                                className={`w-full py-5 rounded-[1.75rem] font-black text-lg transition-all shadow-xl shadow-red-100 flex items-center justify-center ${isSubmitting || isUploading || hasPendingPayment || (proofUrl === '' && !(activeTab === 'subscription' && selectedPlan === 'commission' && restaurant.subscriptionType !== 'commission' && view.params?.mode !== 'renew')) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700 active:scale-95'}`}
                            >
                                <span className="uppercase italic tracking-tight">{getSubmitButtonLabel()}</span>
                            </button>
                        </div>
                    </div>
                </motion.div>
                )}
                </AnimatePresence>

                {/* Footer Actions */}
                <div className="flex flex-col gap-3 pb-8">
                    <button
                        onClick={() => setView({ screen: 'adminPayments' })}
                        className="w-full py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center justify-center gap-2 hover:text-gray-600 transition-colors"
                    >
                        <History size={14} />
                        Voir l'historique des règlements
                    </button>
                    
                    <a
                        href="https://wa.me/213555555555"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-4 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center gap-2 font-black text-xs uppercase tracking-tight border border-emerald-100"
                    >
                        <MessageSquare size={16} />
                        Contacter le Support Technique
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionScreen;
